<template>
  <div class="lcc-page">
    <header class="lcc-top">
      <div class="brand">
        <div class="logo">龙</div>
        <div>
          <div class="title">龙头指挥中心 V2</div>
          <div class="sub">Leader Command Center</div>
        </div>
        <div class="date-block">
          <el-date-picker
            v-model="tradeDate"
            type="date"
            size="small"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :clearable="false"
            @change="onTradeDateChange"
          />
          <div class="date-nav">
            <button
              type="button"
              :disabled="dateNavLoading || !tradeDate"
              @click="goPrevTradeDay"
            >昨日</button>
            <button
              type="button"
              :disabled="dateNavLoading"
              @click="goTodayTradeDay"
            >今日</button>
            <button
              type="button"
              :disabled="dateNavLoading || !tradeDate"
              @click="goNextTradeDay"
            >下一日</button>
          </div>
        </div>
        <div class="primary-tabs">
          <button
            type="button"
            class="ptab"
            :class="{ on: primaryTab === 'themeProp' }"
            @click="switchPrimaryTab('themeProp')"
          >龙头传播（题材）</button>
          <button
            type="button"
            class="ptab"
            :class="{ on: primaryTab === 'risk' }"
            @click="switchPrimaryTab('risk')"
          >风险模式</button>
        </div>
        <div v-if="cloudReady" class="important-court" title="重要王国">
          <button
            v-for="seat in importantSeats"
            :key="'imp-' + seat.slot"
            type="button"
            class="important-seat"
            :class="['rank-' + seat.no, { empty: !seat.id, on: seat.tabId && mainTab === seat.tabId && importantView }]"
            :disabled="!seat.id"
            @click="openImportantSeat(seat)"
          >
            <span class="seat-no">重要王国{{ seat.no }}号</span>
            <b class="seat-name">{{ seat.label }}</b>
          </button>
        </div>
        <div v-if="!cloudReady" class="cloud-wait">正在读取共用王国…</div>
        <div v-else-if="!importantView" class="kingdom-dock" @mouseleave="kingdomHover = -1">
          <button
            v-for="(tab, i) in kingdomHeaderTabs"
            :key="'kh-' + tab.id"
            type="button"
            class="kchip"
            :class="{ on: primaryTab === 'themeProp' && mainTab === tab.id }"
            :style="kingdomChipStyle(i)"
            @mouseenter="kingdomHover = i"
            @click="openKingdomFromHeader(tab)"
          >{{ tab.label }}</button>
        </div>
      </div>
    </header>

    <template v-if="cloudReady && primaryTab === 'risk'">
      <RiskModeShell
        :trade-date="tradeDate"
        :leaders="monitoringCards"
        :loading="loading"
        :load-error="loadError"
        :catchup-rows-for="catchupRowsForBound"
      />
    </template>

    <template v-else-if="cloudReady && primaryTab === 'themeProp'">
      <div class="toolbar">
        <div class="sec-tabs">
          <button
            v-for="tab in themePropTabs"
            :key="'tp-' + tab.id"
            type="button"
            class="stab"
            :class="{ on: mainTab === tab.id, pending: tab.pending, aux: tab.aux }"
            :disabled="tab.pending"
            :title="tab.title || ''"
            @click="onMainTab(tab)"
          >
            {{ tab.label }}
            <em v-if="!tab.pending && tab.count != null">({{ tab.count }})</em>
            <em v-else-if="tab.pending" class="pending-em">— 待接入</em>
          </button>
        </div>
        <div v-if="mainTab === 'observe'" class="toolbar-right">
          <el-input
            v-model="search"
            size="mini"
            clearable
            placeholder="搜索股票 / 代码"
            prefix-icon="el-icon-search"
            class="search"
          />
          <div class="manual-add">
            <el-autocomplete
              v-model="manualAddQuery"
              size="mini"
              class="manual-ac"
              :fetch-suggestions="queryManualStocks"
              placeholder="代码/名称，手动入监控"
              value-key="label"
              :trigger-on-focus="false"
              @select="onManualStockPick"
            />
            <el-button size="mini" type="primary" plain :loading="manualAddLoading" @click="addManualMonitor">添加</el-button>
          </div>
          <el-select v-model="sortKey" size="mini" class="sort">
            <el-option label="排序：涨停/+5%优先 · 20日前三 + 10日↓" value="d20Top3ThenD10" />
            <el-option label="排序：10日总涨幅↓" value="returnD10" />
            <el-option label="排序：当前板位" value="boardStage" />
            <el-option label="排序：接口顺序" value="apiOrder" />
          </el-select>
          <div class="view-toggle">
            <button type="button" :class="{ on: viewMode === 'grid' }" @click="viewMode = 'grid'">网格</button>
            <button type="button" :class="{ on: viewMode === 'list' }" @click="viewMode = 'list'">列表</button>
          </div>
        </div>
      </div>
      <div v-if="mainTab === 'observe' && manualMonitorList.length" class="manual-chips">
        <span class="manual-lbl">手动监控</span>
        <button
          v-for="m in manualMonitorList"
          :key="m.code"
          type="button"
          class="manual-chip"
          :title="'进入日 ' + m.entryDate"
          @click="removeManualMonitor(m.code)"
        >{{ m.name || m.code }} ×</button>
      </div>

      <template v-if="mainTab === 'observe'">
        <div v-if="loadError" class="empty">{{ loadError }}</div>
        <div v-else-if="loading" class="empty">正在读取 {{ tradeDate }} REAL 收盘候选…</div>
        <div v-else-if="!visibleLeaders.length" class="empty">当前筛选无股票。MISSING / UNKNOWN / 待接入不会显示成虚假 0 龙头。</div>

        <div v-else class="board" :class="viewMode">
          <LeaderColumn
            v-for="(leader, idx) in visibleLeaders"
            :key="leader.leaderId + '-observe'"
            :leader="leader"
            :rank="idx + 1"
            :manual-attr-id="prefs.manualPrimaryAttr[leader.leaderId] || ''"
            :concept-options="conceptOptionsFor(leader)"
            :catchup-rows="catchupRowsFor(leader)"
            :catchup-tab="prefs.catchupTab[leader.leaderId] || 'list'"
            :catchup-expanded="!!prefs.catchupExpanded[leader.leaderId]"
            :catchup-prefix="catchupPrefix(idx)"
            :catchup-status="catchupStatusFor(leader)"
            :leader-first-board="leaderFirstBoardFor(leader)"
            :return-meta="returnMeta"
            :catalog="snapshot.abcdCatalog"
            @manual-attr="setManualAttr(leader.leaderId, $event)"
            @open-evidence="openEvidence(leader)"
            @open-attrs="openAttrs(leader)"
            @catchup-tab="setCatchupTab(leader.leaderId, $event)"
            @catchup-expand="setCatchupExpand(leader.leaderId, $event)"
            @open-catchup="openCatchup(leader, $event)"
          />
        </div>

        <footer class="tips">
          <div>① 进入：近 20 交易日曾达 4B（窗内首次4B为进入日）→ 监控池。创业板（300/301）：8 个交易日内有过一次 20% 涨停封死，且除涨停外还有 3 天≥10% 阳线；或 8 个交易日内 3 次 20% 涨停封死 → 观察池。也可手动添加（进入日=当前 as-of）。</div>
          <div>② 退池：A 三板涨停价破位 · B 修复失败（第4日收盘是红K先保留） · C 新高后三连阴 · E 弱波≤60% 新高后10日破 MA15 · F 两次&gt;5%回撤后再创新低 · D 收盘破 MA30；触发当日退出 → 曾入池。手动票规则相同。</div>
          <div>③ 二次入池：同一窗内退出后，20 个交易日内最高价再破近 20 日最高 → 重新入监控。三板涨停价破位退出后，20 个交易日内再次涨停（涨停收回）→ 重新入观察池。</div>
        </footer>
      </template>

      <KingdomDetailView
        v-else-if="isKingdomDetailTab"
        :kingdom="openedKingdom"
        :leaders="monitoringCards"
        :loading="loading"
        :return-meta="returnMeta"
        :trade-date="tradeDate"
        :limit-up-rows="limitUpRows"
        :limit-up-ready="limitUpReady"
        :concepts-by-code="conceptsByCode"
        :concept-items-by-code="conceptItemsByCode"
        :manual-attrs="prefs.manualPrimaryAttr || {}"
        @ensure-concepts="ensureLeaderConcepts"
        :culture-name="cultureName"
        :kingdom-names="otherKingdomNames"
        :important-slots="importantSlots"
        :important-view="importantView"
        @pin-important="onPinImportant"
        @leave-important="onLeaveImportant"
        @rename-kingdom="onRenameKingdom"
        @release-role="onReleaseRole"
        @update-kingdom="onUpdateKingdom"
        @save-main-attrs="onSaveMainAttrs"
        @exit-war="onExitWar"
      />

      <KingdomBuildBoard
        v-else-if="mainTab === 'kingdom'"
        :leaders="visibleLeaders"
        :loading="loading"
        :load-error="loadError"
        :return-meta="returnMeta"
        :concepts-by-code="conceptsByCode"
        :concept-items-by-code="conceptItemsByCode"
        :trade-date="tradeDate"
        :kingdoms="kingdoms"
        :show-culture="!hideCulture"
        @ensure-concepts="ensureLeaderConcepts"
        @set-manual-attr="setManualAttr"
        @create-kingdom="onCreateKingdom"
        @update-kingdom="onUpdateKingdom"
        @open-kingdom="openKingdomTab"
      />

      <div v-else-if="mainTab === 'gapBoard'" class="lcc-gap-host">
        <GapUpAnalysis
          lcc-board
          :trade-date="tradeDate"
        />
      </div>

      <ThemePropShell
        v-else
        :leaders="visibleLeaders"
        :loading="loading"
        :load-error="loadError"
        :return-meta="returnMeta"
        :concepts-by-code="conceptsByCode"
        :concept-items-by-code="conceptItemsByCode"
        :trade-date="tradeDate"
        :mode="mainTab === 'sectorWar' ? 'sectorWar' : 'default'"
        :manual-attrs="prefs.manualPrimaryAttr || {}"
        @ensure-concepts="ensureLeaderConcepts"
      />
    </template>

    <el-drawer :title="drawerTitle" :visible.sync="drawerVisible" size="420px">
      <div class="drawer-body" v-if="drawerPayload">
        <template v-if="drawerType === 'evidence'">
          <template v-if="drawerPayload.cardKind === 'CANDIDATE' || drawerPayload.cardKind === 'MONITORING'">
            <p>生命周期：{{ drawerPayload.lifecycleState || drawerPayload.attentionLevel || '—' }}</p>
            <p>Episode：{{ drawerPayload.episodeId || '—' }}</p>
            <p>进入：{{ drawerPayload.entryDate || '—' }} · 在池天数 {{ drawerPayload.daysInMonitor != null ? drawerPayload.daysInMonitor : '—' }}</p>
            <p>成员：{{ drawerPayload.membership || '—' }} · 最高板 {{ drawerPayload.maxBoardAsOf != null ? drawerPayload.maxBoardAsOf + 'B' : '—' }}</p>
            <p v-if="drawerPayload.exitDate">退出：{{ drawerPayload.exitDate }} · {{ drawerPayload.exitReason || (drawerPayload.exitInfo && drawerPayload.exitInfo.reason) || '—' }}</p>
            <p>原因：{{ (drawerPayload.attentionReason || []).join(' + ') || '—' }}</p>
            <p>末次4B：{{ drawerPayload.fourBoardDate || '—' }} · {{ drawerPayload.entryReason || '20D' }}</p>
            <p>状态依据：{{ (drawerPayload.evidenceHints || []).join('；') || '—' }}</p>
            <p class="muted">事实层仅展示；不是评分/买卖/龙头确认。</p>
          </template>
          <template v-else>
            <p>身份：待接入（接口 quality=NOT_IMPLEMENTED）。禁止由 ABCD 推断。</p>
            <p>主要证据：待接入（正式 ABCD 未物化，前端不重算）</p>
            <p class="muted">接口枚举保留 NOT_IMPLEMENTED。</p>
          </template>
        </template>
        <template v-else-if="drawerType === 'attrs'">
          <p>题材参考〔{{ (drawerPayload.systemPrimaryAttr && drawerPayload.systemPrimaryAttr.snapshotDate) || '2026-09-22' }}快照，非当日归属〕</p>
          <p>系统主属性：{{ (drawerPayload.systemPrimaryAttr && drawerPayload.systemPrimaryAttr.label) || 'MISSING' }}
            （{{ (drawerPayload.systemPrimaryAttr && drawerPayload.systemPrimaryAttr.source) || 'UNKNOWN' }}
            · {{ (drawerPayload.systemPrimaryAttr && drawerPayload.systemPrimaryAttr.membershipBasis) || 'CURRENT_MAPPING_SNAPSHOT' }}
            · {{ (drawerPayload.systemPrimaryAttr && drawerPayload.systemPrimaryAttr.pitStatus) || 'CURRENT_MAPPING_ONLY' }}）</p>
          <p>NH5：{{ nhText(drawerPayload, 'nh5') }} · NH10：{{ nhText(drawerPayload, 'nh10') }} · NEW_HIGH：{{ nhText(drawerPayload, 'newHigh') }}</p>
          <p>ONE_WORD_FLAG：{{ flagText(drawerPayload.oneWordFlag) }} · STREAK_BEFORE_D：{{ streakText(drawerPayload.oneWordStreakBeforeD) }}</p>
          <p>自由流通市值：{{ metricDetail(drawerPayload.supportingMetrics && drawerPayload.supportingMetrics.freeFloatMktCap) }}</p>
          <p>距离年高涨幅：{{ metricDetail(drawerPayload.supportingMetrics && drawerPayload.supportingMetrics.distYearHigh) }}</p>
          <p>人工主属性（本机）：{{ manualAttrLabel(drawerPayload) || '未选择' }}</p>
          <el-table :data="drawerPayload.allAttrs || []" size="mini" border>
            <el-table-column prop="label" label="属性" />
            <el-table-column prop="source" label="来源" width="90" />
            <el-table-column prop="id" label="id" width="120" />
          </el-table>
        </template>
        <template v-else>
          <p>{{ drawerPayload.name }} {{ drawerPayload.code }}</p>
          <p>总分：{{ catchupScoreDetail(drawerPayload) }}（补涨分析口径 · ≥6 入表）</p>
          <p>概念详情：{{ drawerPayload.conceptDetail || drawerPayload.matchedAttrLabel || '—' }}</p>
          <p class="muted">补涨指标（筹码/市值等）仍待接入。</p>
        </template>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import LeaderColumn from './LeaderColumn.vue'
import RiskModeShell from './RiskMode/RiskModeShell.vue'
import ThemePropShell from './ThemeProp/ThemePropShell.vue'
import KingdomBuildBoard from './ThemeProp/KingdomBuildBoard.vue'
import KingdomDetailView from './ThemeProp/KingdomDetailView.vue'
import GapUpAnalysis from '@/views/GapUpAnalysis/index.vue'

import {
  loadLeaderCommandSnapshot,
  loadLeaderCommandMonitoring,
  emptyErrorSnapshot,
  emptyMonitoringState,
  candidateItemToCard,
  attachReturnRanks,
  searchLeaders,
  sortLeaders,
  qualityLabel,
  pendingLabel,
  DEFAULT_TRADE_DATE
} from '@/services/leaderCommandCenterAdapter'
import { loadLccPrefs, saveLccPrefs } from '@/utils/leaderCommandCenterPrefs'
import stockApi from '@/api/stockApi'
import { CONCEPT_SOURCE_THS, CONCEPT_SOURCE_KPL, CONCEPT_SOURCE_MERGE } from '@/utils/conceptSource'
import { resolveAnalysisConcepts } from '@/utils/conceptFilter'
import { matchCatchupAgainstLimitUp, normCode6 } from '@/utils/lccCatchupMatch'
import { clearThemePropForExited, removeCodeFromAllRelayPools } from './ThemeProp/themePropStorage'
import { pullLccSharedState, installLccSharedSync, persistKingdomTabs, flushLccSharedState, hasPendingLccTabs } from '@/utils/lccSharedState'

const KINGDOM_TABS_KEY = 'lcc.kingdom.tabs.v1'
const HIDE_CULTURE_KEY = 'lcc.kingdom.hideCulture.v1'
const JOIN_KEY = 'lcc.kingdom.sectorJoins.v1'
const CULTURE_ROLE_KEY = 'lcc.kingdom.roles.v1|culture'
const CULTURE_NAME_KEY = 'lcc.kingdom.cultureName.v1'
const IMPORTANT_KEY = 'lcc.kingdom.important.v1'

function loadImportantSlots() {
  try {
    const raw = JSON.parse(localStorage.getItem(IMPORTANT_KEY) || '[]')
    const slots = ['', '', '']
    if (Array.isArray(raw)) {
      for (let i = 0; i < 3; i += 1) slots[i] = raw[i] ? String(raw[i]) : ''
    }
    return slots
  } catch (err) {
    return ['', '', '']
  }
}

function saveImportantSlots(slots) {
  try {
    localStorage.setItem(IMPORTANT_KEY, JSON.stringify((slots || ['', '', '']).slice(0, 3)))
  } catch (err) {
    /* ignore */
  }
}

function loadKingdomTabs() {
  try {
    const raw = JSON.parse(localStorage.getItem(KINGDOM_TABS_KEY) || '[]')
    if (!Array.isArray(raw)) return []
    return raw.filter((item) => item && item.id && item.name)
  } catch (err) {
    return []
  }
}

function saveKingdomTabs(list) {
  persistKingdomTabs(list)
}

function releaseCodeFromSector(code, sectorId) {
  const key = normCode6(code)
  if (!key || key === '000000' || !sectorId) return
  try {
    const raw = JSON.parse(localStorage.getItem(JOIN_KEY) || '{}')
    const next = raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...raw } : {}
    const list = Array.isArray(next[key]) ? next[key].filter((id) => id && id !== sectorId) : []
    if (list.length) next[key] = list
    else delete next[key]
    localStorage.setItem(JOIN_KEY, JSON.stringify(next))
  } catch (err) {
    /* ignore */
  }
}

function releaseSectorJoins(sectorId) {
  if (!sectorId) return
  try {
    const raw = JSON.parse(localStorage.getItem(JOIN_KEY) || '{}')
    const next = {}
    Object.keys(raw && typeof raw === 'object' ? raw : {}).forEach((code) => {
      const list = Array.isArray(raw[code]) ? raw[code].filter((id) => id && id !== sectorId) : []
      if (list.length) next[code] = list
    })
    localStorage.setItem(JOIN_KEY, JSON.stringify(next))
  } catch (err) {
    /* ignore */
  }
  if (sectorId === 'culture') {
    try { localStorage.setItem(CULTURE_ROLE_KEY, '{}') } catch (err) { /* ignore */ }
  }
}

export default {
  name: 'LeaderCommandCenter',
  components: { LeaderColumn, RiskModeShell, ThemePropShell, KingdomBuildBoard, KingdomDetailView, GapUpAnalysis },
  data() {
    return {
      tradeDate: DEFAULT_TRADE_DATE,
      snapshot: emptyErrorSnapshot('', DEFAULT_TRADE_DATE),
      monitoring: emptyMonitoringState(DEFAULT_TRADE_DATE),
      loading: true,
      loadError: '',
      search: '',
      primaryTab: 'themeProp',
      mainTab: 'observe',
      kingdomHover: -1,
      sortKey: 'd20Top3ThenD10',
      viewMode: 'grid',
      prefs: loadLccPrefs(),
      kingdoms: loadKingdomTabs(),
      importantSlots: loadImportantSlots(),
      importantView: false,
      cultureName: localStorage.getItem(CULTURE_NAME_KEY) || '文化传媒',
      hideCulture: localStorage.getItem(HIDE_CULTURE_KEY) === '1',
      drawerVisible: false,
      drawerType: '',
      drawerTitle: '',
      drawerPayload: null,
      dateNavLoading: false,
      // client cache: avoid re-hitting API when flipping dates
      monCache: Object.create(null),
      snapCache: Object.create(null),
      reloadSeq: 0,
      // catchup: tradeDate -> limitUp rows; code -> concepts; cacheKey -> rows
      luByDate: Object.create(null),
      luLoadingByDate: Object.create(null),
      conceptsByCode: Object.create(null),
      conceptItemsByCode: Object.create(null),
      conceptsLoading: Object.create(null),
      catchupByKey: Object.create(null),
      catchupStatusByKey: Object.create(null),
      /** tradeDate -> greedyNodeScore */
      nodeScoreByDate: Object.create(null),
      nodeScoreLoadingByDate: Object.create(null),
      /** catchupCacheKey(without attr) -> { closePrice, freeFloatMarketCapYi, firstLimitUpDate, loading } */
      leaderFbByKey: Object.create(null),
      leaderFbLoadingByKey: Object.create(null),
      manualAddQuery: '',
      manualAddPick: null,
      manualAddLoading: false,
      cloudReady: false
    }
  },
  computed: {
    manualMonitorList() {
      const bag = (this.prefs && this.prefs.manualMonitor) || {}
      return Object.keys(bag)
        .map((k) => bag[k])
        .filter((m) => m && m.code)
        .sort((a, b) => String(a.name || a.code).localeCompare(String(b.name || b.code), 'zh'))
    },
    manualEpisodePayload() {
      return this.manualMonitorList.map((m) => ({
        code: m.code,
        name: m.name || '',
        entryDate: m.entryDate
      }))
    },
    candidateCountText() {
      if (this.loading) return '…'
      if (this.loadError) return 'ERROR'
      const n = this.monitoring && this.monitoring.candidatePool && this.monitoring.candidatePool.count
      return n == null ? 'MISSING' : String(n)
    },
    monitoringCountText() {
      if (this.loading) return '…'
      if (this.loadError) return 'ERROR'
      const n = this.monitoring && this.monitoring.activeMonitoring && this.monitoring.activeMonitoring.count
      return n == null ? 'MISSING' : String(n)
    },
    removedCountText() {
      if (this.loading) return '…'
      if (this.loadError) return 'ERROR'
      const rem =
        (this.monitoring && this.monitoring.removedMonitoring) ||
        (this.monitoring && this.monitoring.removedReview) ||
        null
      const n = rem && rem.count
      return n == null ? 'MISSING' : String(n)
    },
    limitUpReferenceCount() {
      if (this.loading) return '…'
      return (this.snapshot.leaders || []).length
    },
    dataStatusText() {
      if (this.loading) return '…'
      if (this.loadError) return 'ERROR'
      const ds =
        (this.monitoring.meta && this.monitoring.meta.dataStatus) ||
        (this.snapshot.meta && this.snapshot.meta.dataStatus) ||
        'ERROR'
      return qualityLabel(ds)
    },
    bannerText() {
      return (
        'REAL｜as-of ' +
        (this.tradeDate || '') +
        ' · 4板入池，触发退池才出池 · 龙头确认/BUY 待接入'
      )
    },
    returnMeta() {
      const m = this.snapshot.meta || {}
      return Object.assign({}, m.returnWindow || {}, { asofTime: m.asofTime })
    },
    marketMaxBoardText() {
      if (this.loading) return '…'
      if (this.loadError) return '—'
      const n =
        this.monitoring &&
        this.monitoring.meta &&
        this.monitoring.meta.marketMaxBoard
      if (n == null || !Number.isFinite(Number(n)) || Number(n) <= 0) return '—'
      const m = Number(n)
      return `${m}天${m}板`
    },
    candidateCards() {
      const items = (this.monitoring.candidatePool && this.monitoring.candidatePool.items) || []
      return items.map(candidateItemToCard).filter(Boolean)
    },
    monitoringCards() {
      const items = (this.monitoring.activeMonitoring && this.monitoring.activeMonitoring.items) || []
      return attachReturnRanks(items.map(candidateItemToCard).filter(Boolean))
    },
    removedCards() {
      const rem =
        (this.monitoring && this.monitoring.removedMonitoring) ||
        (this.monitoring && this.monitoring.removedReview) ||
        { items: [] }
      return (rem.items || []).map(candidateItemToCard).filter(Boolean)
    },
    /** 龙头传播：观察池 + 监控/曾入 + 王国组建 + PIN + 板块战争 */
    themePropTabs() {
      const mon = this.monitoringCards.length
      return [
        { id: 'observe', label: '观察池', count: mon, pending: false },
        { id: 'monitoring', label: '监控池', count: mon, pending: false },
        { id: 'removed', label: '首入池', count: this.removedCards.length, pending: false },
        { id: 'kingdom', label: '王国组建', count: mon, pending: false },
        { id: 'sectorWar', label: '板块战争', count: mon, pending: false }
      ]
    },
    importantSeats() {
      const slots = this.importantSlots || ['', '', '']
      return [0, 1, 2].map((i) => {
        const id = slots[i] || ''
        const tabId = id === 'culture-kpl' ? 'kingdomDetail:culture-kpl' : (id ? `kingdomDetail:${id}` : '')
        const tab = tabId ? (this.kingdomHeaderTabs || []).find((item) => item && item.id === tabId) : null
        return {
          slot: i,
          no: i + 1,
          id: tab ? id : '',
          tabId: tab ? tab.id : '',
          label: tab ? tab.label : '空位'
        }
      })
    },
    kingdomHeaderTabs() {
      const list = []
      if (!this.hideCulture) list.push({ id: 'kingdomDetail:culture-kpl', label: this.cultureName || '文化传媒' })
      ;(this.kingdoms || []).forEach((item) => {
        if (!item || !item.id || !item.name) return
        list.push({ id: 'kingdomDetail:' + item.id, label: item.name })
      })
      const gap = { id: 'gapBoard', label: '切换' }
      const after = list.findIndex((tab) => tab.label === '汽车零部件')
      if (after >= 0) list.splice(after + 1, 0, gap)
      else list.push(gap)
      return list
    },
    isKingdomDetailTab() {
      return String(this.mainTab || '').indexOf('kingdomDetail:') === 0
    },
    otherKingdomNames() {
      const current = this.mainTab
      return (this.kingdomHeaderTabs || [])
        .filter((tab) => tab && tab.id && tab.id !== 'gapBoard' && tab.id !== current)
        .map((tab) => tab.label)
    },
    openedKingdom() {
      if (this.mainTab === 'kingdomDetail:culture-kpl') return null
      const id = String(this.mainTab || '').replace(/^kingdomDetail:/, '')
      return (this.kingdoms || []).find((item) => item.id === id) || null
    },
    limitUpRows() {
      const rows = this.luByDate[this.tradeDate]
      return Array.isArray(rows) ? rows : []
    },
    limitUpReady() {
      return !!this.tradeDate && Object.prototype.hasOwnProperty.call(this.luByDate, this.tradeDate)
    },
    tabSourceLeaders() {
      if (this.mainTab === 'observe') return this.monitoringCards
      if (this.mainTab === 'fourBoard') return this.candidateCards
      if (this.mainTab === 'monitoring') return this.monitoringCards
      if (this.mainTab === 'sectorWar' || this.mainTab === 'kingdom') return this.monitoringCards
      if (this.mainTab === 'removed') return this.removedCards
      if (this.mainTab === 'limitUpRef') {
        return (this.snapshot.leaders || []).map((l) => ({ ...l, cardKind: 'LIMIT_UP_REF' }))
      }
      return this.monitoringCards
    },
    searchedLeaders() {
      return searchLeaders(this.tabSourceLeaders, this.search)
    },
    visibleLeaders() {
      let list = this.searchedLeaders.slice()
      const seen = new Set()
      list = list.filter((l) => {
        if (seen.has(l.leaderId)) return false
        seen.add(l.leaderId)
        return true
      })
      return sortLeaders(list, this.sortKey)
    },
    catchupRowsForBound() {
      return (leader) => this.catchupRowsFor(leader)
    }
  },
  created() {
    this.bootSharedState()
  },
  watch: {
    visibleLeaders: {
      handler() {
        this.refreshCatchupForVisible()
      },
      deep: false
    },
    tradeDate() {
      this.refreshCatchupForVisible()
      if (String(this.mainTab || '').indexOf('kingdomDetail:') === 0) this.ensureLimitUp(this.tradeDate)
    },
    mainTab(val) {
      if (String(val || '').indexOf('kingdomDetail:') === 0) this.ensureLimitUp(this.tradeDate)
      else this.importantView = false
    }
  },
  methods: {
    async bootSharedState() {
      try {
        await pullLccSharedState()
      } catch (err) {
        /* 服务器暂时读不到时，先用本机已有记录 */
      }
      this.kingdoms = loadKingdomTabs()
      this.importantSlots = loadImportantSlots()
      this.cultureName = localStorage.getItem(CULTURE_NAME_KEY) || '文化传媒'
      this.hideCulture = localStorage.getItem(HIDE_CULTURE_KEY) === '1'
      this.cloudReady = true
      installLccSharedSync({
        onReplace: () => {
          this.kingdoms = loadKingdomTabs()
          this.importantSlots = loadImportantSlots()
          this.cultureName = localStorage.getItem(CULTURE_NAME_KEY) || '文化传媒'
          this.hideCulture = localStorage.getItem(HIDE_CULTURE_KEY) === '1'
        }
      })
      if (hasPendingLccTabs()) flushLccSharedState()
      this.reconcileClearedRolesNow()
      this.reloadAll()
    },
    catchupCacheKey(leader) {
      const id = (leader && (leader.leaderId || leader.code)) || ''
      const attr = (this.prefs.manualPrimaryAttr && this.prefs.manualPrimaryAttr[id]) || ''
      return [this.tradeDate || '', id, attr].join('|')
    },
    catchupRowsFor(leader) {
      const key = this.catchupCacheKey(leader)
      return this.catchupByKey[key] || []
    },
    catchupStatusFor(leader) {
      const id = (leader && (leader.leaderId || leader.code)) || ''
      const attr = (this.prefs.manualPrimaryAttr && this.prefs.manualPrimaryAttr[id]) || ''
      if (!attr) return 'no_attr'
      const key = this.catchupCacheKey(leader)
      return this.catchupStatusByKey[key] || 'none'
    },
    leaderFbCacheKey(leader) {
      const id = (leader && (leader.leaderId || leader.code)) || ''
      return [this.tradeDate || '', id].join('|')
    },
    leaderFirstBoardFor(leader) {
      const key = this.leaderFbCacheKey(leader)
      return this.leaderFbByKey[key] || null
    },
    conceptOptionsFor(leader) {
      const code = normCode6(leader && (leader.code || leader.leaderId))
      const list = this.conceptsByCode[code]
      if (list && list.length) return list
      // 若 prefs 里有旧选中值，保证下拉可见
      const id = (leader && (leader.leaderId || leader.code)) || ''
      const selected = (this.prefs.manualPrimaryAttr && this.prefs.manualPrimaryAttr[id]) || ''
      return selected ? [selected] : []
    },
    async ensureLimitUp(td) {
      const date = td || this.tradeDate
      if (!date) return []
      if (this.luByDate[date]) return this.luByDate[date]
      if (this.luLoadingByDate[date]) return this.luLoadingByDate[date]
      const p = stockApi
        .getLimitUpStocks(date)
        .then((res) => {
          const rows = (res && res.data) || []
          this.$set(this.luByDate, date, rows)
          return rows
        })
        .catch((err) => {
          console.warn('[LCC] limit-up fetch failed', err)
          this.$set(this.luByDate, date, [])
          return []
        })
        .finally(() => {
          this.$delete(this.luLoadingByDate, date)
        })
      this.$set(this.luLoadingByDate, date, p)
      return p
    },
    conceptBundle(hit) {
      const local = hit && hit.localConcepts
      const wc = [...new Set(resolveAnalysisConcepts(hit && hit.concepts, [], CONCEPT_SOURCE_THS))]
      const kpl = [...new Set(resolveAnalysisConcepts([], hit && hit.kplConcepts, CONCEPT_SOURCE_KPL))]
      const fixed = [...new Set(resolveAnalysisConcepts([], [], CONCEPT_SOURCE_THS, local))]
      const seen = new Set()
      const items = []
      wc.forEach((name) => {
        if (seen.has(name)) return
        seen.add(name)
        items.push({ name, source: 'WC' })
      })
      kpl.forEach((name) => {
        if (seen.has(name)) return
        seen.add(name)
        items.push({ name, source: 'KPL' })
      })
      fixed.forEach((name) => {
        if (seen.has(name)) return
        seen.add(name)
        items.push({ name, source: '固定' })
      })
      const merged = resolveAnalysisConcepts(hit && hit.concepts, hit && hit.kplConcepts, CONCEPT_SOURCE_MERGE, local)
      return { items, merged }
    },
    async ensureLeaderConcepts(leader) {
      const code = normCode6(leader && (leader.code || leader.leaderId))
      if (!code) return []
      if (this.conceptsByCode[code] && this.conceptItemsByCode[code]) return this.conceptsByCode[code]
      if (this.conceptsLoading[code]) return this.conceptsLoading[code]

      const fromLu = async () => {
        const rows = await this.ensureLimitUp(this.tradeDate)
        const hit = (rows || []).find((r) => normCode6(r.code || r.ts_code) === code)
        if (!hit) return null
        const bundle = this.conceptBundle(hit)
        return bundle.merged.length ? bundle : null
      }

      const p = fromLu()
        .then(async (fromPool) => {
          if (fromPool && fromPool.merged && fromPool.merged.length) return fromPool
          const res = await stockApi.searchStocks(code)
          const data = (res && res.data) || []
          const hit =
            data.find((s) => normCode6(s.code || s.ts_code) === code) || data[0] || null
          if (!hit) return { items: [], merged: [] }
          return this.conceptBundle(hit)
        })
        .then((bundle) => {
          const items = (bundle && bundle.items) || []
          const merged = (bundle && bundle.merged) || []
          this.$set(this.conceptItemsByCode, code, items)
          this.$set(this.conceptsByCode, code, merged)
          return merged
        })
        .catch((err) => {
          console.warn('[LCC] concepts fetch failed', code, err)
          this.$set(this.conceptItemsByCode, code, [])
          this.$set(this.conceptsByCode, code, [])
          return []
        })
        .finally(() => {
          this.$delete(this.conceptsLoading, code)
        })
      this.$set(this.conceptsLoading, code, p)
      return p
    },
    async ensureNodeScore(tradeDate) {
      const date = String(tradeDate || '').trim()
      if (!date) return null
      if (Object.prototype.hasOwnProperty.call(this.nodeScoreByDate, date)) {
        return this.nodeScoreByDate[date]
      }
      if (this.nodeScoreLoadingByDate[date]) return this.nodeScoreLoadingByDate[date]
      const p = stockApi
        .fetchDailyData(date)
        .then((row) => {
          const score =
            row && row.greedyNodeScore != null && Number.isFinite(Number(row.greedyNodeScore))
              ? Number(row.greedyNodeScore)
              : null
          this.$set(this.nodeScoreByDate, date, score)
          return score
        })
        .catch((err) => {
          console.warn('[LCC] nodeScore fetch failed', date, err)
          this.$set(this.nodeScoreByDate, date, null)
          return null
        })
        .finally(() => {
          this.$delete(this.nodeScoreLoadingByDate, date)
        })
      this.$set(this.nodeScoreLoadingByDate, date, p)
      return p
    },
    async enrichCatchupNodeScores(rows) {
      const list = Array.isArray(rows) ? rows : []
      const dates = [
        ...new Set(
          list
            .map((r) => String((r && r.firstBoardDate) || '').trim())
            .filter(Boolean)
        )
      ]
      if (!dates.length) return list
      await Promise.all(dates.map((d) => this.ensureNodeScore(d)))
      return list.map((r) => {
        const fb = String((r && r.firstBoardDate) || '').trim()
        const nodeScore = fb && Object.prototype.hasOwnProperty.call(this.nodeScoreByDate, fb)
          ? this.nodeScoreByDate[fb]
          : null
        const ns = nodeScore != null && Number.isFinite(Number(nodeScore)) ? Number(nodeScore) : null
        return {
          ...r,
          nodeScore: ns,
          nodeHit: ns != null && ns >= 7
        }
      })
    },
    async ensureLeaderFirstBoard(leader) {
      if (!leader) return null
      const key = this.leaderFbCacheKey(leader)
      if (this.leaderFbByKey[key] && !this.leaderFbByKey[key].loading) {
        return this.leaderFbByKey[key]
      }
      if (this.leaderFbLoadingByKey[key]) return this.leaderFbLoadingByKey[key]
      const code = normCode6(leader.code || leader.leaderId)
      const refDate = this.tradeDate || ''
      this.$set(this.leaderFbByKey, key, {
        closePrice: null,
        freeFloatMarketCapYi: null,
        firstLimitUpDate: '',
        loading: true
      })
      const p = stockApi
        .getFirstBoardLeaderFreeFloat(code, refDate)
        .then((res) => {
          const meta = {
            closePrice:
              res && res.closePrice != null && Number.isFinite(Number(res.closePrice))
                ? Number(res.closePrice)
                : null,
            freeFloatMarketCapYi:
              res &&
              res.freeFloatMarketCapYi != null &&
              Number.isFinite(Number(res.freeFloatMarketCapYi))
                ? Number(res.freeFloatMarketCapYi)
                : null,
            firstLimitUpDate: (res && res.firstLimitUpDate) || '',
            loading: false
          }
          this.$set(this.leaderFbByKey, key, meta)
          return meta
        })
        .catch((err) => {
          console.warn('[LCC] leader first-board meta failed', code, err)
          const meta = {
            closePrice: null,
            freeFloatMarketCapYi: null,
            firstLimitUpDate: '',
            loading: false
          }
          this.$set(this.leaderFbByKey, key, meta)
          return meta
        })
        .finally(() => {
          this.$delete(this.leaderFbLoadingByKey, key)
        })
      this.$set(this.leaderFbLoadingByKey, key, p)
      return p
    },
    async refreshCatchupForLeader(leader) {
      if (!leader) return
      const id = leader.leaderId || leader.code
      const attr = (this.prefs.manualPrimaryAttr && this.prefs.manualPrimaryAttr[id]) || ''
      const key = this.catchupCacheKey(leader)
      this.ensureLeaderFirstBoard(leader)
      if (!attr) {
        this.$set(this.catchupStatusByKey, key, 'no_attr')
        this.$set(this.catchupByKey, key, [])
        return
      }
      if (this.catchupByKey[key] && this.catchupStatusByKey[key] === 'ready') return
      this.$set(this.catchupStatusByKey, key, 'loading')
      try {
        const [luRows, concepts] = await Promise.all([
          this.ensureLimitUp(this.tradeDate),
          this.ensureLeaderConcepts(leader)
        ])
        // key 可能因切换日期失效
        const key2 = this.catchupCacheKey(leader)
        if (key2 !== key) return
        let rows = matchCatchupAgainstLimitUp({
          leaderName: leader.name,
          leaderCode: leader.code || id,
          leaderConcepts: concepts,
          selectedConcept: attr,
          limitUpRows: luRows
        })
        rows = await this.enrichCatchupNodeScores(rows)
        if (this.catchupCacheKey(leader) !== key2) return
        this.$set(this.catchupByKey, key2, rows)
        this.$set(this.catchupStatusByKey, key2, 'ready')
      } catch (err) {
        console.warn('[LCC] catchup match failed', id, err)
        this.$set(this.catchupByKey, key, [])
        this.$set(this.catchupStatusByKey, key, 'ready')
      }
    },
    refreshCatchupForVisible() {
      const list = this.visibleLeaders || []
      // 先拉当日涨停池，再并行各卡概念/匹配/龙头首板
      this.ensureLimitUp(this.tradeDate).then(() => {
        list.forEach((leader) => {
          this.ensureLeaderConcepts(leader)
          this.ensureLeaderFirstBoard(leader)
          this.refreshCatchupForLeader(leader)
        })
      })
    },
    onCreateKingdom(payload) {
      const incoming = payload || {}
      const list = (this.kingdoms || []).slice()
      const idx = list.findIndex((item) => item.id === incoming.id || item.name === incoming.name)
      const keptId = idx >= 0 ? list[idx].id : incoming.id
      const row = { ...incoming, id: keptId }
      if (idx >= 0) list.splice(idx, 1, row)
      else list.push(row)
      this.kingdoms = list
      saveKingdomTabs(list)
      this.mainTab = 'kingdomDetail:' + keptId
      this.$message.success(`已打开王国 ${row.name}`)
    },
    async onSaveMainAttrs(payload) {
      const incoming = payload || {}
      if (!incoming.id) return
      const slots = Array.isArray(incoming.mainAttrSlots) ? incoming.mainAttrSlots.slice(0, 3) : []
      while (slots.length < 3) slots.push('')
      const list = (this.kingdoms || []).slice()
      const idx = list.findIndex((item) => item.id === incoming.id)
      if (idx < 0) {
        this.$message.warning('当前王国不在列表里，没能保存')
        return
      }
      list.splice(idx, 1, { ...list[idx], mainAttrSlots: slots.map((item) => String(item || '').trim()) })
      this.kingdoms = list
      saveKingdomTabs(list)
      try {
        const res = await flushLccSharedState()
        if (res && res.ok === false) {
          this.$message.warning(res.message || '服务器没有收下这次保存')
          return
        }
        this.$message.success(incoming.cleared ? '已清空保存的概念' : '主属性已写入服务器')
      } catch (err) {
        this.$message.warning('已留在本机，服务器暂时没写上')
      }
    },
    onUpdateKingdom(patch) {
      const incoming = patch || {}
      if (!incoming.id) return
      const list = (this.kingdoms || []).slice()
      const idx = list.findIndex((item) => item.id === incoming.id)
      if (idx < 0) return
      list.splice(idx, 1, { ...list[idx], roles: incoming.roles, members: incoming.members })
      this.kingdoms = list
      saveKingdomTabs(list)
    },
    onRenameKingdom(name) {
      const next = String(name || '').replace(/\s+/g, '')
      if (!next) return
      if (this.mainTab === 'kingdomDetail:culture-kpl') {
        this.cultureName = next
        try { localStorage.setItem(CULTURE_NAME_KEY, next) } catch (err) { /* ignore */ }
        this.$message.success(`已重命名为 ${next}`)
        return
      }
      const id = String(this.mainTab || '').replace(/^kingdomDetail:/, '')
      const list = (this.kingdoms || []).slice()
      const idx = list.findIndex((item) => item.id === id)
      if (idx < 0) return
      list.splice(idx, 1, { ...list[idx], name: next })
      this.kingdoms = list
      saveKingdomTabs(list)
      this.$message.success(`已重命名为 ${next}`)
    },
    onReleaseRole(payload) {
      const incoming = payload || {}
      const sectorId = incoming.sectorId === 'culture-kpl' ? 'culture' : incoming.sectorId
      const code = normCode6(incoming.code)
      const roleKey = incoming.roleKey
      if (!sectorId || !code || code === '000000') return
      if (sectorId !== 'culture') {
        const list = (this.kingdoms || []).slice()
        const idx = list.findIndex((item) => item.id === sectorId)
        if (idx >= 0) {
          const row = {
            ...list[idx],
            roles: { ...(list[idx].roles || {}) },
            members: Array.isArray(list[idx].members) ? list[idx].members.slice() : []
          }
          if (roleKey && normCode6(row.roles[roleKey]) === code) row.roles[roleKey] = ''
          row.members = row.members.filter((item) => normCode6(item && item.code) !== code)
          list.splice(idx, 1, row)
          this.kingdoms = list
          saveKingdomTabs(list)
        }
      } else {
        try {
          const raw = JSON.parse(localStorage.getItem(CULTURE_ROLE_KEY) || 'null')
          if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw[code]) {
            delete raw[code]
            localStorage.setItem(CULTURE_ROLE_KEY, JSON.stringify(raw))
          }
        } catch (err) {
          /* ignore */
        }
      }
      releaseCodeFromSector(code, sectorId)
      removeCodeFromAllRelayPools(code)
    },
    reconcileClearedRolesNow() {
      const prefix = 'lcc.kingdom.roleOverride.v1|'
      const list = (this.kingdoms || []).slice()
      let changed = false
      list.forEach((kingdom, idx) => {
        if (!kingdom || !kingdom.id) return
        let raw = null
        try {
          raw = JSON.parse(localStorage.getItem(prefix + kingdom.id) || 'null')
        } catch (err) {
          raw = null
        }
        if (!raw || typeof raw !== 'object') return
        const roles = { leader: '', trend: '', board: '', ...(kingdom.roles || {}) }
        const dropped = []
        ;['trend', 'board'].forEach((key) => {
          const ov = raw[key]
          if (!ov || !ov.cleared) return
          const code = normCode6(roles[key] || ov.code)
          if (!code || code === '000000') return
          dropped.push(code)
          roles[key] = ''
          releaseCodeFromSector(code, kingdom.id)
          removeCodeFromAllRelayPools(code)
        })
        if (!dropped.length) return
        changed = true
        const members = (kingdom.members || []).filter((item) => dropped.indexOf(normCode6(item && item.code)) < 0)
        list.splice(idx, 1, { ...kingdom, roles, members })
      })
      if (!changed) return
      this.kingdoms = list
      saveKingdomTabs(list)
    },
    onExitWar() {
      const culture = this.mainTab === 'kingdomDetail:culture-kpl'
      const sectorId = culture ? 'culture' : String(this.mainTab || '').replace(/^kingdomDetail:/, '')
      releaseSectorJoins(sectorId)
      if (culture) {
        this.hideCulture = true
        try { localStorage.setItem(HIDE_CULTURE_KEY, '1') } catch (err) { /* ignore */ }
      } else {
        const list = (this.kingdoms || []).filter((item) => item.id !== sectorId)
        this.kingdoms = list
        saveKingdomTabs(list)
      }
      this.mainTab = 'kingdom'
      const importantId = culture ? 'culture-kpl' : sectorId
      const slots = (this.importantSlots || ['', '', '']).map((item) => (item === importantId ? '' : item))
      this.importantSlots = slots
      saveImportantSlots(slots)
      this.$message.success('已退出战争，板块内龙头已回到龙头监控')
    },
    onPinImportant(payload) {
      const slot = Number(payload && payload.slot)
      if (!(slot === 0 || slot === 1 || slot === 2)) return
      const culture = this.mainTab === 'kingdomDetail:culture-kpl'
      const id = culture ? 'culture-kpl' : String(this.mainTab || '').replace(/^kingdomDetail:/, '')
      if (!id || id === 'kingdomDetail:') return
      const slots = (this.importantSlots || ['', '', '']).slice(0, 3)
      while (slots.length < 3) slots.push('')
      for (let i = 0; i < 3; i += 1) {
        if (i !== slot && slots[i] === id) slots[i] = ''
      }
      const title = `重要王国${slot + 1}号`
      if (slots[slot] === id) {
        slots[slot] = ''
        this.importantView = slots.indexOf(id) >= 0
        this.$message.success(`已移出${title}`)
      } else {
        slots[slot] = id
        this.importantView = true
        this.$message.success(`已进入${title}`)
      }
      this.importantSlots = slots
      saveImportantSlots(slots)
    },
    onLeaveImportant() {
      this.importantView = false
      this.$message.success('已退出重要王国')
    },
    openImportantSeat(seat) {
      if (!seat || !seat.tabId) return
      this.importantView = true
      this.openKingdomFromHeader({ id: seat.tabId }, { keepImportant: true })
    },
    openKingdomTab(tabId) {
      if (!tabId) return
      this.mainTab = tabId
    },
    openKingdomFromHeader(tab, options) {
      if (!tab || !tab.id) return
      this.primaryTab = 'themeProp'
      this.mainTab = tab.id
      if (!(options && options.keepImportant)) this.importantView = false
    },
    openGapBoard() {
      this.primaryTab = 'themeProp'
      this.mainTab = 'gapBoard'
    },
    kingdomChipStyle(index) {
      const gradients = [
        'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 48%, #c4b5fd 100%)',
        'linear-gradient(135deg, #0f766e 0%, #14b8a6 52%, #99f6e4 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 48%, #fde68a 100%)',
        'linear-gradient(135deg, #be123c 0%, #fb7185 50%, #fecdd3 100%)',
        'linear-gradient(135deg, #1d4ed8 0%, #38bdf8 52%, #bae6fd 100%)',
        'linear-gradient(135deg, #166534 0%, #4ade80 50%, #bbf7d0 100%)',
        'linear-gradient(135deg, #9a3412 0%, #fb923c 50%, #ffedd5 100%)',
        'linear-gradient(135deg, #3730a3 0%, #818cf8 52%, #e0e7ff 100%)'
      ]
      const style = {
        background: gradients[index % gradients.length],
        zIndex: this.kingdomHover === index ? 3 : 1
      }
      const hover = this.kingdomHover
      if (hover < 0) return style
      if (hover === index) {
        style.transform = 'scale(1.08) translateY(-3px)'
        return style
      }
      const dist = index - hover
      const sign = dist > 0 ? 1 : -1
      const mag = Math.max(8, 28 - (Math.abs(dist) - 1) * 8)
      style.transform = `translateX(${sign * mag}px)`
      return style
    },
    onMainTab(tab) {
      if (tab && tab.pending) return
      const prev = this.mainTab
      this.mainTab = tab.id
      // lazy-load 涨停池参考 snapshot only when opened
      if (tab.id === 'limitUpRef' && prev !== 'limitUpRef') {
        const td = this.tradeDate || DEFAULT_TRADE_DATE
        if (!this.snapCache[td]) this.reloadAll()
      }
      if (tab.id === 'observe' && prev !== 'observe') {
        this.refreshCatchupForVisible()
      }
    },
    onTradeDateChange() {
      this.reloadAll()
    },
    async applyTradeDate(td) {
      if (!td || td === this.tradeDate) return
      this.tradeDate = td
      await this.reloadAll()
    },
    async goPrevTradeDay() {
      if (!this.tradeDate || this.dateNavLoading) return
      this.dateNavLoading = true
      try {
        const res = await stockApi.prevHistoryFirstBoardTradeDate(this.tradeDate)
        if (!res || !res.ok || !res.prevTradeDate) {
          this.$message.warning((res && res.message) || '无上一交易日')
          return
        }
        await this.applyTradeDate(res.prevTradeDate)
      } catch (err) {
        this.$message.error((err && err.message) || '获取上一交易日失败')
      } finally {
        this.dateNavLoading = false
      }
    },
    async goNextTradeDay() {
      if (!this.tradeDate || this.dateNavLoading) return
      this.dateNavLoading = true
      try {
        const res = await stockApi.nextHistoryFirstBoardTradeDate(this.tradeDate)
        if (!res || !res.ok || !res.nextTradeDate) {
          this.$message.warning((res && res.message) || '无下一交易日')
          return
        }
        await this.applyTradeDate(res.nextTradeDate)
      } catch (err) {
        this.$message.error((err && err.message) || '获取下一交易日失败')
      } finally {
        this.dateNavLoading = false
      }
    },
    async goTodayTradeDay() {
      if (this.dateNavLoading) return
      this.dateNavLoading = true
      try {
        const res = await stockApi.resolveHistoryFirstBoardTradeDate(null)
        if (!res || !res.ok || !res.tradeDate) {
          this.$message.warning((res && res.message) || '无法解析今日交易日')
          return
        }
        await this.applyTradeDate(res.tradeDate)
      } catch (err) {
        this.$message.error((err && err.message) || '跳转今日失败')
      } finally {
        this.dateNavLoading = false
      }
    },
    isMissingAsOfDataError(err) {
      const msg = String(
        (err && (err.message || (err.data && (err.data.error || err.data.message)))) || err || ''
      )
      return (
        /DailyData missing/i.test(msg) ||
        /not in trading_calendar/i.test(msg) ||
        /calendar as-of .+ incomplete/i.test(msg)
      )
    },
    async reloadAll() {
      this.loading = true
      this.loadError = ''
      const seq = ++this.reloadSeq
      const requestedTd = this.tradeDate || DEFAULT_TRADE_DATE
      let td = requestedTd
      let lastErr = null
      const maxFallback = 15

      try {
        for (let attempt = 0; attempt < maxFallback; attempt++) {
          if (seq !== this.reloadSeq) return
          try {
            const monKey = this.monCacheKey(td)
            let mon = this.monCache[monKey]
            let snap = this.snapCache[td]
            const needSnap = this.mainTab === 'limitUpRef'
            const tasks = []
            if (!mon) {
              tasks.push(
                loadLeaderCommandMonitoring(td, this.manualEpisodePayload).then((m) => {
                  mon = m
                  this.$set(this.monCache, monKey, m)
                })
              )
            }
            if (needSnap && !snap) {
              tasks.push(
                loadLeaderCommandSnapshot(td)
                  .then((s) => {
                    snap = s
                    this.$set(this.snapCache, td, s)
                  })
                  .catch((err) => {
                    console.warn('[LCC] snapshot aux failed', err)
                    snap = emptyErrorSnapshot((err && err.message) || 'snapshot aux failed', td)
                  })
              )
            }
            if (tasks.length) await Promise.all(tasks)
            if (seq !== this.reloadSeq) return
            if (!mon) throw new Error('monitoring unavailable')
            if (mon.demoMode) throw new Error('DEMO fallback forbidden')
            this.monitoring = mon
            const removedItems = []
              .concat((mon.removedMonitoring && mon.removedMonitoring.items) || [])
              .concat((mon.removedReview && mon.removedReview.items) || [])
            clearThemePropForExited(removedItems, td)
            if (snap) {
              if (snap.demoMode) throw new Error('DEMO fallback forbidden')
              this.snapshot = snap
            }
            if (td !== this.tradeDate) this.tradeDate = td
            if (seq === this.reloadSeq) {
              this.$nextTick(() => this.refreshCatchupForVisible())
            }
            return
          } catch (err) {
            lastErr = err
            if (!this.isMissingAsOfDataError(err)) throw err
            const res = await stockApi.prevHistoryFirstBoardTradeDate(td)
            const prev = res && res.ok ? res.prevTradeDate : null
            if (!prev) throw err
            const staleKey = this.monCacheKey(td)
            if (this.monCache[staleKey]) this.$delete(this.monCache, staleKey)
            if (this.snapCache[td]) this.$delete(this.snapCache, td)
            td = prev
          }
        }
        throw lastErr || new Error('无可用交易日数据')
      } catch (err) {
        if (seq !== this.reloadSeq) return
        const msg = (err && (err.message || (err.data && err.data.error))) || 'REAL monitoring failed'
        this.loadError = 'ERROR｜' + msg
        this.monitoring = emptyMonitoringState(td)
        this.snapshot = emptyErrorSnapshot(msg, td)
      } finally {
        if (seq === this.reloadSeq) this.loading = false
      }
    },
    persist() {
      this.prefs = saveLccPrefs(this.prefs)
    },
    clearMonCache() {
      this.monCache = Object.create(null)
    },
    monCacheKey(td) {
      const fp = this.manualEpisodePayload
        .map((m) => m.code + ':' + m.entryDate)
        .sort()
        .join(',')
      return String(td || '') + '|' + fp
    },
    queryManualStocks(query, cb) {
      const q = String(query || '').trim()
      if (!q) {
        cb([])
        return
      }
      stockApi
        .searchStocks(q)
        .then((res) => {
          const data = (res && res.data) || res || []
          const rows = (Array.isArray(data) ? data : []).slice(0, 12).map((s) => {
            const code = normCode6(s.code || s.ts_code)
            const name = String(s.name || '').trim()
            return {
              code,
              name,
              value: code,
              label: name ? `${name} ${code}` : code
            }
          }).filter((r) => r.code)
          cb(rows)
        })
        .catch(() => cb([]))
    },
    onManualStockPick(item) {
      this.manualAddPick = item || null
      this.manualAddQuery = (item && item.label) || this.manualAddQuery
    },
    async addManualMonitor() {
      if (this.manualAddLoading) return
      this.manualAddLoading = true
      try {
        let code = this.manualAddPick && this.manualAddPick.code
        let name = (this.manualAddPick && this.manualAddPick.name) || ''
        const q = String(this.manualAddQuery || '').trim()
        if (!code) {
          const m = q.match(/(\d{6})/)
          code = m ? m[1] : ''
        }
        if (!code && q) {
          const res = await stockApi.searchStocks(q)
          const data = (res && res.data) || res || []
          const hit = (Array.isArray(data) ? data : [])[0]
          code = normCode6(hit && (hit.code || hit.ts_code))
          name = String((hit && hit.name) || '').trim()
        }
        if (!code) {
          this.$message.warning('请输入有效股票代码或名称')
          return
        }
        if (!name) {
          try {
            const res = await stockApi.searchStocks(code)
            const data = (res && res.data) || res || []
            const hit = (Array.isArray(data) ? data : []).find((s) => normCode6(s.code || s.ts_code) === code) || (Array.isArray(data) ? data : [])[0]
            name = String((hit && hit.name) || '').trim()
          } catch (_err) {
            /* ignore */
          }
        }
        const entryDate = this.tradeDate || DEFAULT_TRADE_DATE
        if (!this.prefs.manualMonitor || typeof this.prefs.manualMonitor !== 'object') {
          this.$set(this.prefs, 'manualMonitor', {})
        }
        this.$set(this.prefs.manualMonitor, code, { code, name: name || code, entryDate })
        this.persist()
        this.manualAddQuery = ''
        this.manualAddPick = null
        this.clearMonCache()
        this.$message.success(`${name || code} 已加入手动监控（进入日 ${entryDate}）`)
        await this.reloadAll()
      } catch (err) {
        this.$message.error((err && err.message) || '添加失败')
      } finally {
        this.manualAddLoading = false
      }
    },
    async removeManualMonitor(code) {
      const c = normCode6(code)
      if (!c || !this.prefs.manualMonitor || !this.prefs.manualMonitor[c]) return
      this.$delete(this.prefs.manualMonitor, c)
      this.persist()
      this.clearMonCache()
      this.$message.info(`已移除手动监控 ${c}`)
      await this.reloadAll()
    },
    setManualAttr(leaderId, attrId) {
      if (!attrId) this.$delete(this.prefs.manualPrimaryAttr, leaderId)
      else this.$set(this.prefs.manualPrimaryAttr, leaderId, attrId)
      this.persist()
      const leader = (this.visibleLeaders || []).find(
        (l) => l.leaderId === leaderId || l.code === leaderId
      )
      if (leader) {
        // 清掉旧 key 状态，强制重算
        const key = this.catchupCacheKey(leader)
        this.$delete(this.catchupByKey, key)
        this.$delete(this.catchupStatusByKey, key)
        this.refreshCatchupForLeader(leader)
      } else {
        this.refreshCatchupForVisible()
      }
    },
    setCatchupTab(leaderId, tab) {
      this.$set(this.prefs.catchupTab, leaderId, tab)
      this.persist()
    },
    setCatchupExpand(leaderId, on) {
      this.$set(this.prefs.catchupExpanded, leaderId, !!on)
      this.persist()
    },
    switchPrimaryTab(tab) {
      const t = String(tab || 'themeProp')
      if (t === 'themeProp' || t === 'risk') {
        this.primaryTab = t
        if (t === 'themeProp' && !['observe', 'monitoring', 'removed', 'kingdom', 'sectorWar', 'gapBoard'].includes(this.mainTab) && String(this.mainTab || '').indexOf('kingdomDetail:') !== 0) {
          this.mainTab = 'observe'
        }
        return
      }
      this.primaryTab = 'themeProp'
    },
    catchupPrefix(idx) {
      return String.fromCharCode(65 + (idx % 26))
    },
    openEvidence(leader) {
      this.drawerType = 'evidence'
      this.drawerTitle = leader.name + (leader.cardKind === 'CANDIDATE' ? ' · 候选证据' : ' · ABCD 证据')
      this.drawerPayload = leader
      this.drawerVisible = true
    },
    openAttrs(leader) {
      this.drawerType = 'attrs'
      this.drawerTitle = leader.name + ' · 全部属性'
      this.drawerPayload = leader
      this.drawerVisible = true
    },
    openCatchup(leader, row) {
      this.drawerType = 'catchup'
      this.drawerTitle = leader.name + ' · 补涨详情 ' + row.name
      this.drawerPayload = row
      this.drawerVisible = true
    },
    manualAttrLabel(leader) {
      const id = this.prefs.manualPrimaryAttr[leader.leaderId]
      return id || ''
    },
    catchupScoreDetail(row) {
      if (!row) return '—'
      if (row.totalScore != null) return String(row.totalScore)
      if (row.matchScore && row.matchScore.value != null) return String(row.matchScore.value)
      return '—'
    },
    metricDetail(m) {
      if (!m) return '待接入'
      if (m.quality === 'MISSING') return 'MISSING'
      if (m.quality === 'UNKNOWN') return 'UNKNOWN'
      if (m.quality === 'NOT_IMPLEMENTED') return '待接入'
      if (m.value === null || m.value === undefined) return pendingLabel(m.quality) || '—'
      return String(m.value) + (m.unit || '') + ' · ' + (m.fieldName || '') + ' · ' + (m.note || m.quality || '')
    },
    nhText(leader, key) {
      const block = leader && leader.nh && leader.nh[key]
      if (!block) return 'MISSING'
      if (block.quality && block.quality !== 'VALID') return block.quality
      if (block.value === null || block.value === undefined) return block.quality || 'MISSING'
      if (typeof block.value === 'boolean') return block.value ? 'YES' : 'NO'
      return String(block.value)
    },
    flagText(block) {
      if (!block) return 'UNKNOWN'
      if (block.quality && block.quality !== 'VALID') return block.quality
      if (block.value === true) return 'YES'
      if (block.value === false) return 'NO'
      return 'UNKNOWN'
    },
    streakText(block) {
      if (!block) return 'UNKNOWN'
      if (block.quality && block.quality !== 'VALID') return block.quality
      if (block.value === null || block.value === undefined) return block.quality || 'MISSING'
      return String(block.value)
    }
  }
}
</script>

<style lang="scss" scoped>
.lcc-page {
  min-height: 100%;
  margin: -20px;
  padding: 12px 16px 24px;
  background: #eef4fb;
  color: #1a2b3c;
}
.lcc-top {
  background: #fff;
  border: 1px solid #d7e4f6;
  border-radius: 12px;
  padding: 10px 14px;
}
.brand { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; width: 100%; }
.date-block { margin-left: 4px; }
.logo {
  width: 32px; height: 32px; border-radius: 10px;
  background: linear-gradient(180deg, #3b89f0, #1a73e8);
  color: #fff; font-weight: 800; display: flex; align-items: center; justify-content: center;
}
.title { font-size: 16px; font-weight: 800; }
.sub { font-size: 11px; color: #8aa0c2; }
.important-court {
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-left: 8px;
  padding: 5px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fff9ee 0%, #f6e4bc 100%);
  border: 1px solid #e7c56a;
  box-shadow: 0 6px 16px rgba(196, 146, 42, 0.16), inset 0 1px 0 #fff;
}
.important-seat {
  position: relative;
  overflow: hidden;
  min-width: 112px;
  min-height: 40px;
  padding: 5px 12px 6px;
  border-radius: 12px;
  border: 1px solid rgba(212, 164, 64, 0.55);
  background: linear-gradient(135deg, #fffdf8 0%, #f4e6c4 100%);
  color: #6a4a16;
  text-align: left;
  cursor: pointer;
}
.important-seat .seat-no,
.important-seat .seat-name { position: relative; z-index: 1; }
.important-seat.rank-1 {
  background: linear-gradient(135deg, #fff4c8 0%, #f6c044 46%, #e08a12 100%);
  border-color: #e2a020;
  color: #5c2e00;
  box-shadow: 0 6px 14px rgba(210, 130, 20, 0.28);
}
.important-seat.rank-2 {
  background: linear-gradient(135deg, #fffaf0 0%, #f3d78a 58%, #e4bc62 100%);
  border-color: #e0c07a;
  color: #6a4e1c;
}
.important-seat.rank-3 {
  background: linear-gradient(135deg, #fffdf8 0%, #f7edd6 100%);
  border-color: #ead7a4;
  color: #8a7048;
}
.important-seat.rank-1::after,
.important-seat.rank-2::after,
.important-seat.rank-3::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-repeat: no-repeat;
}
.important-seat.rank-1::after {
  background-image:
    radial-gradient(circle, #fff 0 1.2px, transparent 1.8px),
    radial-gradient(circle, #fff8d0 0 1.4px, transparent 2.2px),
    radial-gradient(circle, #fff 0 1px, transparent 1.6px),
    radial-gradient(circle, rgba(255, 255, 255, 0.95) 0 1px, transparent 1.6px);
  background-position: 16% 28%, 78% 18%, 62% 72%, 88% 58%;
  animation: seat-twinkle 1.6s ease-in-out infinite;
}
.important-seat.rank-2::after {
  background-image:
    radial-gradient(circle, #fff 0 1px, transparent 1.6px),
    radial-gradient(circle, #fff6d8 0 1.1px, transparent 1.8px);
  background-position: 22% 32%, 80% 64%;
  animation: seat-twinkle 2.4s ease-in-out infinite;
}
.important-seat.rank-3::after {
  background-image: radial-gradient(circle, #fff 0 1px, transparent 1.5px);
  background-position: 70% 30%;
  animation: seat-twinkle 3.2s ease-in-out infinite;
}
.important-seat.empty {
  opacity: 0.62;
  cursor: default;
  box-shadow: none;
}
.important-seat.empty::after { display: none; }
.important-seat.on {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #e2a020, 0 8px 16px rgba(210, 140, 30, 0.25);
}
.important-seat .seat-no {
  display: block;
  font-size: 10px;
  letter-spacing: 0.12em;
  color: #a06820;
}
.important-seat.rank-1 .seat-no { color: #8a4b00; }
.important-seat .seat-name {
  display: block;
  margin-top: 1px;
  font-size: 13px;
  font-weight: 800;
  color: #5c3a10;
}
.important-seat.rank-1 .seat-name { color: #4a2400; }
@keyframes seat-twinkle {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
}
.primary-tabs { margin-left: 12px; display: flex; gap: 8px; }
.cloud-wait {
  margin-left: auto;
  color: #8aa0c2;
  font-size: 12px;
}
.kingdom-dock {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 38px;
  padding: 2px 4px;
}
.kchip {
  border: 0;
  color: #fff;
  border-radius: 16px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  text-shadow: 0 1px 1px rgba(40, 20, 10, 0.25);
  box-shadow: 0 4px 12px rgba(20, 40, 80, 0.14);
  transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.28s ease;
  &.on {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(26, 115, 232, 0.55), 0 6px 14px rgba(20, 40, 80, 0.18);
  }
}
.lcc-gap-host {
  margin-top: 10px;
  height: calc(100vh - 156px);
  min-height: 480px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
}
.ptab {
  border: 0;
  background: #eef3fb;
  color: #5b6b82;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  &.on { background: #1a73e8; color: #fff; font-weight: 700; }
}
.meta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
}
.date-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 150px;
  :deep(.el-date-editor.el-input),
  :deep(.el-date-editor.el-input__inner) {
    width: 100% !important;
  }
  :deep(.el-input) {
    width: 100% !important;
  }
}
.date-nav {
  display: flex;
  width: 100%;
  gap: 4px;
  box-sizing: border-box;
  button {
    flex: 1 1 0;
    min-width: 0;
    width: 0;
    border: 1px solid #d7e4f6;
    background: #f7faff;
    color: #35598c;
    border-radius: 6px;
    padding: 3px 0;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    line-height: 1.3;
    text-align: center;
    box-sizing: border-box;
    &:hover:not(:disabled) {
      background: #e8f1ff;
      border-color: #1a73e8;
      color: #1a73e8;
    }
    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }
}
.stat {
  min-width: 72px;
  b { display: block; font-size: 18px; color: #1a73e8; }
  span { font-size: 11px; color: #90a4c0; }
  &.aux b { color: #7b93b5; font-size: 16px; }
}
.stab.aux { border-style: dashed; }
.search { width: 220px; margin-left: auto; }
.status-banner {
  margin: 8px 0;
  background: #eef6ff;
  color: #1a4f8b;
  border: 1px solid #cfe0f8;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  background: #fff;
  border: 1px solid #d7e4f6;
  border-radius: 12px;
  padding: 8px 10px;
}
.sec-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.stab {
  border: 1px solid #d7e4f6;
  background: #f7faff;
  color: #35598c;
  border-radius: 16px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  em { font-style: normal; color: #8aa0c2; }
  &.on { background: #1a73e8; color: #fff; border-color: #1a73e8; em { color: #d6e6ff; } }
  &.pending {
    opacity: 0.72;
    cursor: not-allowed;
    color: #7b93b5;
  }
  .pending-em { color: #ad6800; font-style: normal; }
}
.toolbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.manual-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.manual-ac { width: 180px; }
.manual-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
  padding: 0 2px;
}
.manual-lbl {
  font-size: 11px;
  color: #8aa0c2;
  font-weight: 700;
}
.manual-chip {
  border: 1px solid #c5daf5;
  background: #eef5ff;
  color: #1a73e8;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  cursor: pointer;
  &:hover { border-color: #f56c6c; color: #f56c6c; background: #fff5f5; }
}
.hint { font-size: 12px; color: #90a4c0; }
.hint-ico { color: #1a73e8; cursor: help; }
.sort { width: 150px; }
.view-toggle {
  display: flex;
  border: 1px solid #d7e4f6;
  border-radius: 8px;
  overflow: hidden;
  button {
    border: 0;
    background: #fff;
    color: #5b6b82;
    padding: 4px 8px;
    cursor: pointer;
    font-size: 12px;
    &.on { background: #e8f1ff; color: #1a73e8; font-weight: 700; }
  }
}
.board {
  display: grid;
  grid-template-columns: repeat(4, minmax(280px, 1fr));
  gap: 12px;
  margin-top: 12px;
  overflow-x: auto;
  align-items: start;
  &.list {
    grid-template-columns: 1fr;
    max-width: 720px;
  }
}
.empty {
  margin-top: 16px;
  color: #7b93b5;
  font-size: 13px;
}
.tips {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  font-size: 12px;
  color: #5b6b82;
  div { background: #fff; border-radius: 10px; padding: 10px; border: 1px solid #d7e4f6; }
}
.drawer-body {
  padding: 0 16px 24px;
  font-size: 13px;
  line-height: 1.55;
  color: #3a4d66;
}
.muted { color: #90a4c0; }
@media (max-width: 1280px) {
  .board.grid { grid-template-columns: repeat(2, minmax(280px, 1fr)); }
  .tips { grid-template-columns: 1fr; }
}
@media (max-width: 900px) {
  .brand { align-items: flex-start; }
  .important-court { flex-wrap: wrap; margin-left: 0; }
  .important-seat { min-width: 96px; min-height: 44px; }
  .kchip { min-height: 36px; }
  .kingdom-dock { margin-left: 0; flex-wrap: wrap; }
}
</style>
